import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Topbar from "../components/Topbar";
import { useAuth } from "../context/AuthContext";
import { listHotels } from "../api/hotels";
import DataUnavailableCard, { TECHNICAL_ERROR_MESSAGE } from "./analyticsDashboard/components/DataUnavailableCard";
import DemoAnalyticsBanner from "./analyticsDashboard/components/DemoAnalyticsBanner";
import DiffDateTablesCard from "./analyticsDashboard/components/DiffDateTablesCard";
import FiltersLoadingPlaceholder from "./analyticsDashboard/components/FiltersLoadingPlaceholder";
import GuestCountDisplay from "./analyticsDashboard/components/GuestCountDisplay";
import LoadingOverlay from "./analyticsDashboard/components/LoadingOverlay";
import ObjectSelectCard from "./analyticsDashboard/components/ObjectSelectCard";
import DateDeltaFiltersSection, {
  DateDeltaFiltersValue,
} from "./analyticsDashboard/components/DateDeltaFiltersSection";
import type { DiffDashboardColumn, DiffDashboardHotelRow } from "./analyticsDashboard/diffTypes";
import {
  ApiFilterOption,
  ApiHotelFilterOption,
  downloadPriceWithDeltaXlsx,
  FiltersResponse,
  getFilters,
  getMinPriceWithDelta,
  MinPriceWithDeltaResponse,
} from "./analyticsDashboard/api";
import {
  DEMO_ANALYTICS_GROUP_ID,
  DEMO_ANALYTICS_OBJECT_OPTION,
  DEMO_ANALYTICS_OBJECT_VALUE,
} from "./analyticsDashboard/demoObject";
import {
  LAST_PARSING_DATE_LABEL,
  isLastParsingDateInfoReady,
  useLastParsingDateInfo,
} from "./analyticsDashboard/hooks/useLastParsingDate";
import {
  isAvailableParsingDatesInfoReady,
  normalizeAvailableDate,
  useAvailableParsingDates,
} from "./analyticsDashboard/hooks/useAvailableParsingDates";
import { getSubscriptionState } from "../utils/subscription";
import {
  buildAnalyticsGuestsPayload,
  buildGuestSelectionOptions,
} from "../utils/guests";
import { loadHotelsDisplaySettings } from "../utils/hotelDisplaySettings";

type SelectOption = { label: string; value: string };

type DeltaFilterOptions = {
  category: SelectOption[];
  ota: SelectOption[];
  boardType: SelectOption[];
  refundPolicy: SelectOption[];
  paymentType: SelectOption[];
  hotel: SelectOption[];
};

const DEFAULT_FILTER_OPTIONS: DeltaFilterOptions = {
  category: [],
  ota: [],
  boardType: [],
  refundPolicy: [],
  paymentType: [],
  hotel: [],
};

const MONTH_LABELS = ["янв", "фев", "мар", "апр", "май", "июн", "июл", "авг", "сен", "окт", "ноя", "дек"];
const WEEKDAY_LABELS = ["Вс", "Пн", "Вт", "Ср", "Чт", "Пт", "Сб"];

const formatIsoDate = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getTodayDate = () => formatIsoDate(new Date());
const getWeekAgoDate = () => {
  const date = new Date();
  date.setDate(date.getDate() - 7);
  return formatIsoDate(date);
};
const getMonthAheadDate = () => {
  const date = new Date();
  date.setMonth(date.getMonth() + 1);
  return formatIsoDate(date);
};

const getDefaultFilters = (requestDate = getWeekAgoDate()): DateDeltaFiltersValue => ({
  dateFrom: getTodayDate(),
  dateTo: getMonthAheadDate(),
  category: [],
  ota: [],
  boardType: [],
  refundPolicy: [],
  paymentType: [],
  requestDate,
  hotel: [],
});

const getAppliedAtLabel = () => new Date().toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" });
const formatDateFull = (value: string) => {
  const [year, month, day] = value.split("-");
  if (!year || !month || !day) return value;
  return `${day}.${month}.${year}`;
};

const formatStayLabel = (daysStay: number) => {
  if (daysStay === 1) return "1 ночь";
  if (daysStay < 5) return `${daysStay} ночи`;
  return `${daysStay} ночей`;
};

const toSelectOptions = (
  items?: Array<ApiFilterOption | string> | Record<string, string>,
): SelectOption[] => {
  if (!items) return [];
  if (!Array.isArray(items)) {
    return Object.entries(items).map(([key, value]) => ({ label: value, value: key }));
  }

  return items
    .map((item) => {
      if (typeof item === "string") return { label: item, value: item };
      if (!item) return null;
      const value = item.key ?? item.value;
      const label = item.value ?? item.key;
      if (!value || !label) return null;
      return { label, value };
    })
    .filter((item): item is SelectOption => Boolean(item));
};

const toHotelFilterOptions = (
  items?: Array<ApiFilterOption | ApiHotelFilterOption | string> | Record<string, string>,
): SelectOption[] => {
  if (!items) return [];
  if (!Array.isArray(items)) {
    return Object.values(items).map((value) => ({ label: value, value }));
  }

  return items
    .map((item) => {
      if (typeof item === "string") return { label: item, value: item };
      if (!item) return null;
      if ("hotel_name" in item) {
        return { label: item.hotel_name, value: item.hotel_name };
      }
      const label = item.value ?? item.key;
      if (!label) return null;
      return { label, value: label };
    })
    .filter((item): item is SelectOption => Boolean(item));
};

const toColumn = (isoDate: string): DiffDashboardColumn => {
  const date = new Date(`${isoDate}T00:00:00`);
  return {
    isoDate,
    shortLabel: `${String(date.getDate()).padStart(2, "0")}.${MONTH_LABELS[date.getMonth()]}`,
    weekDay: WEEKDAY_LABELS[date.getDay()],
  };
};

const mapResponseToBoard = (response: MinPriceWithDeltaResponse): { columns: DiffDashboardColumn[]; rows: DiffDashboardHotelRow[] } => {
  const sortedDates = Object.keys(response.dates ?? {}).sort((left, right) => left.localeCompare(right));
  const pricesByDate = sortedDates.reduce<Record<string, Map<string, { price_today?: number | null; price_past?: number | null; delta?: number | null }>>>(
    (acc, dateKey) => {
      acc[dateKey] = new Map(
        (response.dates[dateKey] ?? []).map((item) => [String(item.hotel_id), item.prices ?? {}]),
      );
      return acc;
    },
    {},
  );

  return {
    columns: sortedDates.map(toColumn),
    rows: response.hotels.map((hotel) => {
      const hotelId = String(hotel.hotel_id);
      return {
        hotelName: hotel.hotel_name,
        prices: sortedDates.map((dateKey) => pricesByDate[dateKey]?.get(hotelId)?.price_today ?? null),
        deltas: sortedDates.map((dateKey) => {
          const prices = pricesByDate[dateKey]?.get(hotelId);
          if (!prices || prices.price_past === null || prices.price_past === undefined) return null;
          return prices.delta ?? null;
        }),
      };
    }),
  };
};

export default function AnalyticsDeltaByDatePage() {
  const { user, company_id: companyId } = useAuth();
  const subscriptionState = getSubscriptionState(user?.subscription_expires_on);
  const isSubscriptionExpired = subscriptionState.isExpired;

  const [selectedObjectId, setSelectedObjectId] = useState("");
  const [daysStay, setDaysStay] = useState<number>(1);
  const [guests, setGuests] = useState<string>("2");
  const [hotelOptions, setHotelOptions] = useState<SelectOption[]>([]);
  const [hotelGroupMap, setHotelGroupMap] = useState<Record<string, number | null>>({});
  const [hotelMinStayMap, setHotelMinStayMap] = useState<Record<string, number[] | null>>({});
  const [hotelGuestsMap, setHotelGuestsMap] = useState<Record<string, number[] | null>>({});
  const [hotelGuestsChildMap, setHotelGuestsChildMap] = useState<Record<string, number[] | null>>({});
  const [selectedObjectHasCompetitors, setSelectedObjectHasCompetitors] = useState<boolean | null>(null);
  const [filterOptions, setFilterOptions] = useState<DeltaFilterOptions>(DEFAULT_FILTER_OPTIONS);
  const [filters, setFilters] = useState<DateDeltaFiltersValue>(getDefaultFilters());
  const [appliedFilters, setAppliedFilters] = useState<DateDeltaFiltersValue | null>(null);
  const [columns, setColumns] = useState<DiffDashboardColumn[]>([]);
  const [rows, setRows] = useState<DiffDashboardHotelRow[]>([]);
  const [appliedAt, setAppliedAt] = useState<string | null>(null);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [noData, setNoData] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const requestIdRef = useRef(0);
  const lastDefaultDateRef = useRef<string | null>(filters.requestDate);

  const selectedGroupId = useMemo(() => {
    if (!selectedObjectId) return null;
    return hotelGroupMap[selectedObjectId] ?? null;
  }, [hotelGroupMap, selectedObjectId]);
  const selectedHotelMinStay = useMemo(() => {
    if (!selectedObjectId) return null;
    return hotelMinStayMap[selectedObjectId] ?? null;
  }, [hotelMinStayMap, selectedObjectId]);
  const selectedHotelGuests = useMemo(() => {
    if (!selectedObjectId) return null;
    return hotelGuestsMap[selectedObjectId] ?? null;
  }, [hotelGuestsMap, selectedObjectId]);
  const selectedHotelGuestsChild = useMemo(() => {
    if (!selectedObjectId) return null;
    return hotelGuestsChildMap[selectedObjectId] ?? null;
  }, [hotelGuestsChildMap, selectedObjectId]);
  const selectedHotelMinStayOptions = selectedHotelMinStay ?? [];
  const selectedHotelGuestOptions = useMemo(
    () => buildGuestSelectionOptions(selectedHotelGuests, selectedHotelGuestsChild),
    [selectedHotelGuests, selectedHotelGuestsChild],
  );
  const hasSelectedHotelMinStay = Boolean(selectedHotelMinStay?.length);
  const hasSelectedHotelGuests = Boolean(selectedObjectId);
  const hasObjectSelected = Boolean(selectedObjectId);
  const isDemoAnalytics = selectedObjectId === DEMO_ANALYTICS_OBJECT_VALUE;
  const shouldBlockAnalyticsRequests = hasObjectSelected && !isDemoAnalytics && selectedObjectHasCompetitors !== true;
  const lastParsingDate = useLastParsingDateInfo(selectedGroupId, daysStay, !isSubscriptionExpired);
  const isLastParsingDateReady = isLastParsingDateInfoReady(
    lastParsingDate,
    selectedGroupId,
    daysStay,
    !isSubscriptionExpired,
  );
  const availableParsingDates = useAvailableParsingDates(selectedGroupId, daysStay, guests, !isSubscriptionExpired);
  const isAvailableParsingDatesReady = isAvailableParsingDatesInfoReady(
    availableParsingDates,
    selectedGroupId,
    daysStay,
    guests,
    !isSubscriptionExpired,
  );
  const defaultDataCollectionDate = lastParsingDate.date ?? getWeekAgoDate();
  const selectedParsingDate = filters.requestDate || defaultDataCollectionDate;
  const appliedParsingDate = appliedFilters?.requestDate || defaultDataCollectionDate;
  const shouldBlockParsingDateRequests = Boolean(selectedGroupId) && (
    !isAvailableParsingDatesReady ||
    Boolean(availableParsingDates.error) ||
    availableParsingDates.dates.length === 0 ||
    !availableParsingDates.dateSet.has(selectedParsingDate)
  );
  const shouldBlockAppliedParsingDateRequests = Boolean(selectedGroupId) && (
    !isAvailableParsingDatesReady ||
    Boolean(availableParsingDates.error) ||
    availableParsingDates.dates.length === 0 ||
    !availableParsingDates.dateSet.has(appliedParsingDate)
  );
  const filtersLoading = hasObjectSelected && (optionsLoading || availableParsingDates.loading);
  const shouldWaitForDefaultDate =
    isLastParsingDateReady &&
    Boolean(lastParsingDate.date) &&
    appliedFilters !== null &&
    appliedFilters.requestDate !== lastParsingDate.date &&
    (!appliedFilters.requestDate ||
      appliedFilters.requestDate === lastDefaultDateRef.current);
  const selectedObjectLabel = useMemo(
    () => hotelOptions.find((option) => option.value === selectedObjectId)?.label ?? "Календарь цен",
    [hotelOptions, selectedObjectId],
  );
  const tableSubtitle = useMemo(() => {
    if (!appliedFilters) return undefined;
    const parts = [`Дата парсинга: ${formatDateFull(appliedFilters.requestDate)}`, `Срок заселения: ${formatStayLabel(daysStay)}`];
    if (appliedFilters.dateFrom || appliedFilters.dateTo) {
      parts.unshift(
        `Дата заселения: ${formatDateFull(appliedFilters.dateFrom || "—")} - ${formatDateFull(appliedFilters.dateTo || "—")}`,
      );
    }
    return parts.join(" · ");
  }, [appliedFilters, daysStay]);

  useEffect(() => {
    if (!selectedHotelMinStay?.length) {
      setDaysStay(1);
      return;
    }
    if (!selectedHotelMinStay.includes(daysStay)) {
      setDaysStay(selectedHotelMinStay[0]);
    }
  }, [daysStay, selectedHotelMinStay]);

  useEffect(() => {
    if (!selectedHotelGuestOptions.some((option) => option.value === guests)) {
      setGuests(selectedHotelGuestOptions[0]?.value ?? "2");
    }
  }, [guests, selectedHotelGuestOptions]);

  useEffect(() => {
    const lastDate = lastParsingDate.date;
    if (!lastDate) return;

    const currentDefaultDate = lastDefaultDateRef.current;
    const shouldApplyDefault = (requestDate: string) =>
      !requestDate || requestDate === currentDefaultDate;

    setFilters((prev) => {
      if (!selectedObjectId || !shouldApplyDefault(prev.requestDate) || prev.requestDate === lastDate) return prev;
      return { ...prev, requestDate: lastDate };
    });
    setAppliedFilters((prev) => {
      if (!prev || !selectedObjectId || !shouldApplyDefault(prev.requestDate) || prev.requestDate === lastDate) return prev;
      return { ...prev, requestDate: lastDate };
    });
    if (selectedObjectId) {
      lastDefaultDateRef.current = lastDate;
    }
  }, [lastParsingDate.date, selectedObjectId]);

  useEffect(() => {
    if (!selectedGroupId || !isAvailableParsingDatesReady || availableParsingDates.error) return;
    setFilters((prev) => {
      if (!selectedObjectId) return prev;
      const nextDate = normalizeAvailableDate(prev.requestDate, defaultDataCollectionDate, availableParsingDates.dates);
      if (prev.requestDate === nextDate) return prev;
      return { ...prev, requestDate: nextDate };
    });
    setAppliedFilters((prev) => {
      if (!prev || !selectedObjectId) return prev;
      const nextDate = normalizeAvailableDate(prev.requestDate, defaultDataCollectionDate, availableParsingDates.dates);
      if (prev.requestDate === nextDate) return prev;
      return { ...prev, requestDate: nextDate };
    });
  }, [
    availableParsingDates.dates,
    availableParsingDates.datesKey,
    availableParsingDates.error,
    defaultDataCollectionDate,
    isAvailableParsingDatesReady,
    selectedGroupId,
    selectedObjectId,
  ]);

  useEffect(() => {
    if (!companyId) return;
    let isMounted = true;
    setOptionsLoading(true);

    listHotels(companyId, { page: 1, per_page: 200 })
      .then(async (response) => {
        if (!isMounted) return;
        const nextHotelOptions = response.items.map((item) => ({ label: item.name, value: String(item.id) }));
        if (response.items.length === 0) {
          const nextFilters = getDefaultFilters();
          lastDefaultDateRef.current = nextFilters.requestDate;
          setHotelOptions([DEMO_ANALYTICS_OBJECT_OPTION]);
          setHotelGroupMap({ [DEMO_ANALYTICS_OBJECT_VALUE]: DEMO_ANALYTICS_GROUP_ID });
          setHotelMinStayMap({ [DEMO_ANALYTICS_OBJECT_VALUE]: null });
          setHotelGuestsMap({ [DEMO_ANALYTICS_OBJECT_VALUE]: null });
          setHotelGuestsChildMap({ [DEMO_ANALYTICS_OBJECT_VALUE]: null });
          setSelectedObjectId(DEMO_ANALYTICS_OBJECT_VALUE);
          setError(null);
          setNoData(false);
          setColumns([]);
          setRows([]);
          setFilterOptions(DEFAULT_FILTER_OPTIONS);
          setFilters(nextFilters);
          setAppliedFilters(nextFilters);
          setAppliedAt(getAppliedAtLabel());
          return;
        }
        const displaySettingsMap = await loadHotelsDisplaySettings(response.items);
        if (!isMounted) return;
        const nextHotelGroupMap = response.items.reduce<Record<string, number | null>>((acc, item) => {
          acc[String(item.id)] = item.group_id ?? null;
          return acc;
        }, {});
        const nextHotelMinStayMap = response.items.reduce<Record<string, number[] | null>>((acc, item) => {
          acc[String(item.id)] = displaySettingsMap[String(item.id)]?.min_stay ?? item.min_stay ?? null;
          return acc;
        }, {});
        const nextHotelGuestsMap = response.items.reduce<Record<string, number[] | null>>((acc, item) => {
          acc[String(item.id)] = displaySettingsMap[String(item.id)]?.guests ?? item.guests ?? null;
          return acc;
        }, {});
        const nextHotelGuestsChildMap = response.items.reduce<Record<string, number[] | null>>((acc, item) => {
          acc[String(item.id)] = displaySettingsMap[String(item.id)]?.guests_child ?? item.guests_child ?? null;
          return acc;
        }, {});
        setHotelOptions(nextHotelOptions);
        setHotelGroupMap(nextHotelGroupMap);
        setHotelMinStayMap(nextHotelMinStayMap);
        setHotelGuestsMap(nextHotelGuestsMap);
        setHotelGuestsChildMap(nextHotelGuestsChildMap);
        if (response.items.length === 1) {
          const nextFilters = getDefaultFilters();
          lastDefaultDateRef.current = nextFilters.requestDate;
          setSelectedObjectId(String(response.items[0].id));
          setError(null);
          setNoData(false);
          setColumns([]);
          setRows([]);
          setFilterOptions(DEFAULT_FILTER_OPTIONS);
          setFilters(nextFilters);
          setAppliedFilters(nextFilters);
          setAppliedAt(getAppliedAtLabel());
          return;
        }
        setNoData(false);
        setSelectedObjectId((prev) => (nextHotelOptions.some((item) => item.value === prev) ? prev : ""));
      })
      .catch(() => {
        if (!isMounted) return;
        setError("Не удалось загрузить список объектов");
      })
      .finally(() => {
        if (isMounted) setOptionsLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [companyId]);

  useEffect(() => {
    if (!selectedGroupId) {
      setFilterOptions(DEFAULT_FILTER_OPTIONS);
      setSelectedObjectHasCompetitors(null);
      setNoData(false);
      return;
    }

    let isMounted = true;
    setOptionsLoading(true);
    setSelectedObjectHasCompetitors(isDemoAnalytics ? true : null);
    setError(null);

    getFilters(selectedGroupId)
      .then((response: FiltersResponse) => {
        if (!isMounted) return;
        const hotelFilterOptions = toHotelFilterOptions(response.hotel);
        const hasCompetitors = Array.isArray(response.hotel)
          ? response.hotel.some((item) => {
              if (typeof item === "string") return true;
              if (!item) return false;
              if ("hotel_name" in item) {
                return !(item.is_base === true || item.is_base === 1 || item.is_base === "1");
              }
              return true;
            })
          : hotelFilterOptions.length > 0;

        setSelectedObjectHasCompetitors(isDemoAnalytics || hasCompetitors);
        setFilterOptions({
          category: toSelectOptions(response.category),
          ota: toSelectOptions(response.ota),
          boardType: toSelectOptions(response.board_type),
          refundPolicy: toSelectOptions(response.refund_policy),
          paymentType: toSelectOptions(response.payment_type),
          hotel: hotelFilterOptions,
        });
      })
      .catch(() => {
        if (!isMounted) return;
        setError("Не удалось загрузить фильтры");
        setColumns([]);
        setRows([]);
      })
      .finally(() => {
        if (isMounted) setOptionsLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [isDemoAnalytics, selectedGroupId]);

  const fetchDeltaMetrics = async (currentFilters: DateDeltaFiltersValue) => {
    if (!selectedGroupId || isSubscriptionExpired) {
      setLoading(false);
      setNoData(false);
      setError(null);
      setColumns([]);
      setRows([]);
      return;
    }
    if (shouldBlockAnalyticsRequests) {
      setLoading(false);
      setNoData(false);
      setError(null);
      setColumns([]);
      setRows([]);
      return;
    }
    const requestDate = currentFilters.requestDate || defaultDataCollectionDate;
    if (!availableParsingDates.dateSet.has(requestDate)) {
      setLoading(false);
      setNoData(false);
      setColumns([]);
      setRows([]);
      return;
    }
    const requestId = ++requestIdRef.current;
    setLoading(true);
    setNoData(false);
    setError(null);

    try {
      const response = await getMinPriceWithDelta({
        group_id: selectedGroupId,
        date_from: currentFilters.dateFrom || undefined,
        date_to: currentFilters.dateTo || undefined,
        category: currentFilters.category.length ? currentFilters.category : undefined,
        ota: currentFilters.ota.length ? currentFilters.ota : undefined,
        board_type: currentFilters.boardType.length ? currentFilters.boardType : undefined,
        refund_policy: currentFilters.refundPolicy.length ? currentFilters.refundPolicy : undefined,
        payment_type: currentFilters.paymentType.length ? currentFilters.paymentType : undefined,
        request_date: requestDate,
        hotel: currentFilters.hotel.length ? currentFilters.hotel : undefined,
        days_stay: daysStay > 1 ? daysStay : undefined,
        ...buildAnalyticsGuestsPayload(guests),
      });
      if (requestId !== requestIdRef.current) return;
      const mapped = mapResponseToBoard(response);
      setNoData(mapped.columns.length === 0 || mapped.rows.length === 0);
      setColumns(mapped.columns);
      setRows(mapped.rows);
    } catch {
      if (requestId !== requestIdRef.current) return;
      setNoData(false);
      setError("Не удалось загрузить дельту по дате");
      setColumns([]);
      setRows([]);
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  };

  const handleDownloadXlsx = async () => {
    if (!selectedGroupId || !appliedFilters || downloadLoading || isSubscriptionExpired || shouldBlockAnalyticsRequests) return;
    if (shouldBlockAppliedParsingDateRequests) return;

    setDownloadLoading(true);

    try {
      const { blob, filename } = await downloadPriceWithDeltaXlsx({
        group_id: selectedGroupId,
        date_from: appliedFilters.dateFrom || undefined,
        date_to: appliedFilters.dateTo || undefined,
        category: appliedFilters.category.length ? appliedFilters.category : undefined,
        ota: appliedFilters.ota.length ? appliedFilters.ota : undefined,
        board_type: appliedFilters.boardType.length ? appliedFilters.boardType : undefined,
        refund_policy: appliedFilters.refundPolicy.length ? appliedFilters.refundPolicy : undefined,
        payment_type: appliedFilters.paymentType.length ? appliedFilters.paymentType : undefined,
        request_date: appliedFilters.requestDate || defaultDataCollectionDate,
        hotel: appliedFilters.hotel.length ? appliedFilters.hotel : undefined,
        days_stay: daysStay > 1 ? daysStay : undefined,
        ...buildAnalyticsGuestsPayload(guests),
      });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = filename || `price-with-delta-${selectedObjectId || "report"}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (downloadError) {
      console.error("Не удалось скачать отчет дельты по дате", downloadError);
      setError("download_error");
    } finally {
      setDownloadLoading(false);
    }
  };

  useEffect(() => {
    if (
      isSubscriptionExpired ||
      !selectedGroupId ||
      !appliedFilters ||
      shouldBlockAnalyticsRequests ||
      shouldBlockAppliedParsingDateRequests ||
      !isLastParsingDateReady ||
      !isAvailableParsingDatesReady ||
      shouldWaitForDefaultDate
    ) return;
    fetchDeltaMetrics(appliedFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGroupId, appliedFilters, daysStay, guests, isSubscriptionExpired, shouldBlockAnalyticsRequests, shouldBlockAppliedParsingDateRequests, isLastParsingDateReady, isAvailableParsingDatesReady, shouldWaitForDefaultDate]);

  const handleApply = () => {
    if (shouldBlockAnalyticsRequests || shouldBlockParsingDateRequests || !isLastParsingDateReady || !isAvailableParsingDatesReady) return;
    const nextFilters = { ...filters };
    setFilters(nextFilters);
    setAppliedFilters(nextFilters);
    setAppliedAt(getAppliedAtLabel());
  };

  const handleReset = () => {
    if (shouldBlockAnalyticsRequests || !isLastParsingDateReady || !isAvailableParsingDatesReady || availableParsingDates.error) return;
    const resetDate = normalizeAvailableDate(defaultDataCollectionDate, defaultDataCollectionDate, availableParsingDates.dates);
    const nextFilters = getDefaultFilters(resetDate);
    lastDefaultDateRef.current = nextFilters.requestDate;
    setFilters(nextFilters);
    setAppliedFilters(nextFilters);
    setAppliedAt(getAppliedAtLabel());
  };

  const handleObjectChange = (value: string) => {
    const nextFilters = getDefaultFilters();
    lastDefaultDateRef.current = nextFilters.requestDate;
    setSelectedObjectId(value);
    setSelectedObjectHasCompetitors(value === DEMO_ANALYTICS_OBJECT_VALUE ? true : null);
    setError(null);
    setNoData(false);
    setColumns([]);
    setRows([]);
    setFilterOptions(DEFAULT_FILTER_OPTIONS);
    setFilters(nextFilters);
    setAppliedFilters(value ? nextFilters : null);
    setAppliedAt(value ? getAppliedAtLabel() : null);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 via-slate-50 to-slate-100 text-slate-900">
      <Topbar />

      <main className="px-6 py-8">
        <div className="mx-auto flex max-w-[1440px] flex-col gap-6">
          <section className="relative overflow-hidden rounded-3xl border border-white/70 bg-white/90 p-6 shadow-[0_18px_40px_rgba(15,23,42,0.08)] backdrop-blur">
            <div className="pointer-events-none absolute inset-y-0 right-0 w-56 translate-x-10 rounded-full bg-gradient-to-b from-indigo-100 via-violet-100 to-white blur-3xl opacity-80" />
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <h1 className="mt-2 text-3xl font-bold text-slate-900">Календарь цен</h1>
                <p className="mt-2 max-w-3xl text-sm text-slate-500">
                  Дашборд показывает изменение минимальной цены отеля:
                  сравнивает текущую цену на выбранную дату с ценой на ту же дату, зафиксированной N дней назад
                </p>
              </div>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
                <div className="flex flex-col gap-3 self-start">
                  {hasObjectSelected && hasSelectedHotelMinStay && (
                    <div className="flex items-center gap-3 self-start sm:self-auto">
                    <span className="text-xs font-semibold uppercase tracking-[0.08em] text-indigo-500">Срок заселения</span>
                    <div className="inline-flex rounded-full border border-indigo-200 bg-white p-1 shadow-sm">
                      {selectedHotelMinStayOptions.map((value) => {
                        const isActive = daysStay === value;
                        return (
                          <button
                            key={value}
                            type="button"
                            onClick={() => {
                              setDaysStay(value);
                              setAppliedAt(getAppliedAtLabel());
                            }}
                            className={[
                              "rounded-full px-3 py-1 text-xs font-semibold transition",
                              isActive ? "bg-indigo-600 text-white" : "text-slate-600 hover:bg-indigo-50",
                            ].join(" ")}
                            aria-pressed={isActive}
                          >
                            {formatStayLabel(value)}
                          </button>
                        );
                      })}
                    </div>
                    </div>
                  )}
                  {hasObjectSelected && hasSelectedHotelGuests && (
                    <GuestCountDisplay value={guests} options={selectedHotelGuestOptions} onChange={setGuests} />
                  )}
                </div>
                <div className="rounded-2xl border border-indigo-100 bg-indigo-50/70 px-5 py-4 text-sm text-indigo-700">
                  {lastParsingDate.display ? `${LAST_PARSING_DATE_LABEL}: ${lastParsingDate.display}` : appliedAt ? `Параметры обновлены в ${appliedAt}` : "Выберите объект и примените фильтры"}
                </div>
              </div>
            </div>
          </section>

          {error && <DataUnavailableCard message={TECHNICAL_ERROR_MESSAGE} variant="error" />}

          {isDemoAnalytics && <DemoAnalyticsBanner />}

          <ObjectSelectCard value={selectedObjectId} options={hotelOptions} onChange={handleObjectChange} loading={optionsLoading} />

          {hasObjectSelected && selectedGroupId !== null && filtersLoading ? (
            <FiltersLoadingPlaceholder label="Загружаем фильтры и доступные даты..." />
          ) : hasObjectSelected && selectedGroupId !== null && selectedObjectHasCompetitors === true && (
            <DateDeltaFiltersSection
              filters={filters}
              options={filterOptions}
              loading={loading || optionsLoading || !isLastParsingDateReady || shouldBlockAnalyticsRequests}
              applyDisabled={shouldBlockParsingDateRequests}
              parsingDateAvailability={{
                dates: availableParsingDates.dates,
                loading: availableParsingDates.loading,
                error: availableParsingDates.error,
              }}
              onChange={setFilters}
              onApply={handleApply}
              onReset={handleReset}
            />
          )}

          {hasObjectSelected && selectedGroupId === null && selectedObjectHasCompetitors !== false && (
            <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-700">
              У выбранного объекта отсутствует `group_id`, загрузка аналитики недоступна.
            </div>
          )}

          {!hasObjectSelected && (
            <div className="rounded-3xl border border-dashed border-slate-300 bg-white/90 px-6 py-10 text-center text-slate-500">
              Выберите объект размещения, чтобы загрузить дельту по дате.
            </div>
          )}

          {hasObjectSelected && !isSubscriptionExpired && !error && !loading && selectedObjectHasCompetitors === false && (
            <DataUnavailableCard
              message={
                <>
                  Для начала работы добавьте конкурентов в{" "}
                  <Link className="text-indigo-600 underline decoration-indigo-300 underline-offset-4 hover:text-indigo-700" to={`/my/objects/${selectedObjectId}`}>
                    объект размещения
                  </Link>
                </>
              }
            />
          )}

          {hasObjectSelected && !isSubscriptionExpired && selectedGroupId !== null && !error && selectedObjectHasCompetitors === true && (
            <section className="relative">
              <LoadingOverlay visible={loading} label="Загружаем дельту по дате..." />
              <div className={loading ? "pointer-events-none opacity-60" : ""}>
                {!noData && columns.length > 0 && rows.length > 0 ? (
                  <DiffDateTablesCard
                    eyebrow="Аналитика"
                    title={selectedObjectLabel}
                    subtitle={tableSubtitle}
                    columns={columns}
                    rows={rows}
                    onDownload={handleDownloadXlsx}
                    downloadLoading={downloadLoading}
                  />
                ) : noData ? (
                  <DataUnavailableCard />
                ) : null}
              </div>
            </section>
          )}
        </div>
      </main>
    </div>
  );
}
